import "styles/tabSpecRun.css"
import React, { useEffect, useRef, useState } from "react"
import { Alert, Input, Modal, Radio } from "antd"
import { useProjectStore } from "stores/projectStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import { downloadWorkbook } from "utils/excel/exportTableModels"
import DummyNotice from "features/DummyData/components/DummyNotice.component"
import { bookFileName, buildTabBook, collectBook } from "../../export/tabBook"
import {
  TabSpecState,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../../runtime/tabSpecStore"

type Layout = "separate" | "combined"

const LAYOUTS: { value: Layout; label: string }[] = [
  { value: "separate", label: "One sheet per table" },
  { value: "combined", label: "All tables on one sheet" },
]

/** Included tables whose run does not match the current settings. */
const notRunCount = (s: TabSpecState): number =>
  collectBook(s).filter((t) => t.status === "not-run").length

const notRunText = (n: number): string =>
  n === 1
    ? "1 table has not been run with the current settings."
    : `${n} tables have not been run with the current settings.`

export interface DownloadDialogProps {
  open: boolean
  onClose: () => void
  /** After "Run them first" has closed the dialog: opens the run dialog. */
  onRunFirst: () => void
}

/**
 * Downloads the tab book as an Excel file, one sheet per table or all tables
 * on one sheet. The file holds what the current settings give: a table not
 * run with them is listed as "Not run" on the contents sheet, so the dialog
 * says how many there are and offers to run them first.
 */
const DownloadDialog: React.FC<DownloadDialogProps> = ({ open, onClose, onRunFirst }) => {
  const store = useTabSpecStoreApi()
  const specName = useTabSpecStore((s) => s.name)
  // A project without a questionnaire never has dummy data: nothing to read.
  const dummyFor = useTabSpecStore((s) => (s.dataOnly ? undefined : s.projectId))
  const projectName = useProjectStore((s) => s.project.Name)
  // Counted only while the dialog is open: it compiles every table.
  const notRun = useTabSpecStore((s) => (open ? notRunCount(s) : 0))
  const [layout, setLayout] = useState<Layout>("separate")
  const defaultName = bookFileName(projectName, specName)
  const [fileName, setFileName] = useState(defaultName)
  const [busy, setBusy] = useState(false)
  // Set at once, so a second click before the re-render does not download twice.
  const running = useRef(false)
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const dialogContainer = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  // Each opening starts from the default name.
  useEffect(() => {
    if (open) setFileName(defaultName)
  }, [open, defaultName])

  const download = async (): Promise<void> => {
    if (running.current) return
    running.current = true
    setBusy(true)
    try {
      const state = store.getState()
      const workbook = buildTabBook({
        tables: collectBook(state),
        projectName,
        specName: state.name,
        approval: "Draft",
        style: resolveTableStyle("report", state.document.defaults.tableStyle),
        mode: layout,
        exportedAt: new Date(),
      })
      await downloadWorkbook(workbook, fileName.trim() || defaultName)
      onClose()
    } catch (e) {
      notify.error(
        "Tab book",
        e instanceof Error && e.message ? e.message : "The tab book could not be written."
      )
    } finally {
      running.current = false
      setBusy(false)
    }
  }

  const runFirst = (): void => {
    onClose()
    onRunFirst()
  }

  // The platform's footer: Cancel first, the main action last.
  const footer = [
    <DataQButton key="cancel" type="secondary" onClick={onClose}>
      Cancel
    </DataQButton>,
    ...(notRun > 0
      ? [
          <DataQButton
            key="anyway"
            type="secondary"
            isLoading={busy}
            onClick={() => void download()}
          >
            Download anyway
          </DataQButton>,
          <DataQButton key="run" type="primary" onClick={runFirst}>
            Run them first
          </DataQButton>,
        ]
      : [
          <DataQButton
            key="download"
            type="primary"
            isLoading={busy}
            onClick={() => void download()}
          >
            Download
          </DataQButton>,
        ]),
  ]

  return (
    <>
      <span ref={anchorRef} hidden />
      <Modal
        open={open}
        title="Download Excel"
        onCancel={onClose}
        footer={footer}
        getContainer={dialogContainer}
        width={520}
      >
        <div className="ts-download">
          <DummyNotice projectId={dummyFor} />
          <section className="ts-field" aria-label="Layout">
            <h3 className="ts-field__label">Layout</h3>
            <Radio.Group
              aria-label="Layout"
              value={layout}
              onChange={(e) => setLayout(e.target.value as Layout)}
              options={LAYOUTS}
            />
          </section>
          <section className="ts-field" aria-label="File name">
            <h3 className="ts-field__label">File name</h3>
            <Input
              aria-label="File name"
              value={fileName}
              placeholder={defaultName}
              addonAfter=".xlsx"
              onChange={(e) => setFileName(e.target.value)}
            />
          </section>
          {notRun > 0 && (
            <Alert
              type="warning"
              showIcon
              message={notRunText(notRun)}
              description="Download anyway lists them as Not run on the contents sheet."
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default DownloadDialog
